// ============================================================
//  src/admin/pages/AdminDashboardPage.js
// ============================================================
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { getDashboard } from '../api/adminClient';
import { StatCard, PageHeader } from '../components/UI';

function fmtNum(n) { return (Number(n) || 0).toLocaleString('en-NG'); }
function fmtNaira(n) { return '₦' + (Number(n) || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 }); }
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d.replace(' ', 'T'));
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboard()
      .then(res => setData(res.data))
      .catch(err => toast.error(err?.response?.data?.message || 'Could not load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="bf-empty"><span className="spinner spinner-dark" /> Loading dashboard…</div>;
  }
  if (!data) {
    return <div className="bf-empty">Could not load dashboard data.</div>;
  }

  const { stats, plan_distribution, recent_users } = data;

  return (
    <div className="bf-page">
      <PageHeader title="Dashboard" subtitle="A live snapshot of subscribers, bots, and revenue." />

      <div className="bf-stat-grid">
        <StatCard label="Total Users" value={fmtNum(stats.subscribers.total)} sub={`+${stats.subscribers.new_today} today`} />
        <StatCard label="Active Users" value={fmtNum(stats.subscribers.active)} accent="var(--bf-success)" />
        <StatCard label="Suspended Users" value={fmtNum(stats.subscribers.suspended)} accent="var(--bf-danger)" />
        <StatCard label="Unverified" value={fmtNum(stats.subscribers.unverified)} accent="var(--bf-warning)" />
      </div>

      <div className="bf-stat-grid">
        <StatCard label="Total Bots" value={fmtNum(stats.bots.total)} />
        <StatCard label="Active Bots" value={fmtNum(stats.bots.active)} accent="var(--bf-success)" />
        <StatCard label="Suspended Bots" value={fmtNum(stats.bots.suspended)} accent="var(--bf-danger)" />
        <StatCard label="Bot Users (End Users)" value={fmtNum(stats.bot_users.total)} sub={`+${stats.bot_users.new_today} today, ${stats.bot_users.blocked} blocked`} />
      </div>

      <div className="bf-stat-grid">
        <StatCard label="Total Messages Processed" value={fmtNum(stats.messages.total)} />
        <StatCard label="Revenue (All Time)" value={fmtNaira(stats.revenue.total)} accent="var(--bf-success)" />
        <StatCard label="Revenue Today" value={fmtNaira(stats.revenue.today)} />
        <StatCard label="Revenue (30d)" value={fmtNaira(stats.revenue.last_30d)} />
      </div>

      <div className="bf-two-col">
        {/* Plan distribution */}
        <div className="bf-card" style={{ marginBottom: 0 }}>
          <div className="bf-card-hdr">Plan Distribution</div>
          <div className="bf-card-body">
            {plan_distribution.length === 0 ? (
              <div className="bf-muted" style={{ fontSize: 13 }}>No active subscriptions yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {plan_distribution.map(p => (
                  <div key={p.plSlug} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}>
                    <span style={{ fontWeight: 600 }}>{p.plName}</span>
                    <span className="bf-muted">{fmtNum(p.count)} users</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent signups */}
        <div className="bf-card" style={{ marginBottom: 0 }}>
          <div className="bf-card-hdr">Recent Signups</div>
          <div className="bf-card-body">
            {recent_users.length === 0 ? (
              <div className="bf-muted" style={{ fontSize: 13 }}>No users yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {recent_users.map(u => (
                  <div key={u.sId} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.sFname} {u.sLname} <span className="bf-muted">({u.sEmail})</span>
                    </span>
                    <span className="bf-muted" style={{ flexShrink: 0 }}>{fmtDate(u.sCreatedAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}